import { db } from "@/lib/firebase";
import { JobGenerationService } from "@/lib/jobGenerationService";
import { ensureTimestamp } from "@/lib/utils";
import { Job, safeParseHeuristicJob } from "@/schemas/firestore/jobs.schema";
import {
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

/* ---------- slice ---------- */

// heuristic_jobs is backend-write-only (see firestore.rules) -- the client
// only ever reads/subscribes; status transitions and deletion happen
// server-side.
export interface JobsSlice {
  /* state */
  jobs: Record<string, Job>; // Use Job union type
  currentJobId: string | null;
  isLoadingJobs: boolean;
  jobsError: string | null;

  /* actions */
  subscribeToJobs: (
    domainId: string | null | undefined,
  ) => () => void;
  setCurrentJob: (jobId: string) => void;
  startJob: (url: string) => Promise<string>;
  unsubscribeJobs: () => void;

  _jobsSubscriptions: Map<string, () => void>;
}

export const createJobsSlice: StateCreator<StoreState, [], [], JobsSlice> = (
  set,
  get,
) => ({
  /* ---------- initial ---------- */
  jobs: {},
  currentJobId: null,
  isLoadingJobs: false,
  jobsError: null,
  _jobsSubscriptions: new Map(),

  /* ---------- actions ---------- */
  subscribeToJobs: (domainId) => {
    const orgId = get().orgId;
    if (!orgId || !domainId) {
      console.log("Jobs subscription requires orgId and domainId.");
      return () => {};
    }

    // If we're already subscribed to this domain, return the existing unsubscribe function
    const existing = get()._jobsSubscriptions.get(domainId);
    if (existing) {
      console.log(`Jobs subscription already active for domain ${domainId}`);
      return existing;
    }

    set((state) => ({
      isLoadingJobs: Object.keys(state.jobs).length === 0,
      jobsError: null,
    }));

    // Collection group query for all heuristic jobs under this domain
    const heuristicJobsRef = collectionGroup(db, "heuristic_jobs");
    const qHeuristic = query(
      heuristicJobsRef,
      where("org_id", "==", orgId),
      where("domain_id", "==", domainId),
      orderBy("created_at", "desc"),
    );

    console.log(
      `Setting up real-time jobs listener for domain ${domainId} (collection group)`,
    );

    const unsubHeuristic = onSnapshot(
      qHeuristic,
      (snapshot) => {
        const domainJobs: Record<string, Job> = {};
        snapshot.docs.forEach((docSnapshot) => {
          const rawData = docSnapshot.data();
          const data = {
            ...rawData,
            id: docSnapshot.id,
            created_at: ensureTimestamp(rawData.created_at),
            updated_at: ensureTimestamp(rawData.updated_at),
          };

          const result = safeParseHeuristicJob(data);
          if (result.success) {
            domainJobs[docSnapshot.id] = result.data;
          } else {
            console.error(
              `Validation failed for heuristic job ${docSnapshot.id}:`,
              result.error.issues,
            );
            console.error("Raw data:", rawData);
          }
        });
        console.log(
          `Heuristic jobs snapshot: ${snapshot.docs.length} docs, ${Object.keys(domainJobs).length} valid for domain ${domainId}`,
        );
        // Merge with jobs from other domains to support multi-domain views
        set((state) => {
          const merged: Record<string, Job> = {};
          for (const job of Object.values(state.jobs)) {
            if (job.domain_id !== domainId) {
              merged[job.id] = job;
            }
          }
          for (const [id, job] of Object.entries(domainJobs)) {
            merged[id] = job;
          }
          return {
            jobs: merged,
            isLoadingJobs: false,
          };
        });
      },
      (error) => {
        console.error("Error in heuristic_jobs onSnapshot:", error);
        set({
          jobsError: "Failed to listen to heuristic job updates",
          isLoadingJobs: false,
        });
      },
    );

    const wrappedUnsubscribe = () => {
      unsubHeuristic();
      get()._jobsSubscriptions.delete(domainId);
      console.log(`Unsubscribed from jobs for domain ${domainId}`);
    };

    set((state) => {
      const newSubscriptions = new Map(state._jobsSubscriptions);
      newSubscriptions.set(domainId, wrappedUnsubscribe);
      return { _jobsSubscriptions: newSubscriptions };
    });

    return wrappedUnsubscribe;
  },

  setCurrentJob: (jobId) => set({ currentJobId: jobId }),

  /* ---------- startJob ---------- */
  startJob: async (url) => {
    const { currentUser, ensureDomainAndPageExists } = get();
    if (!currentUser) throw new Error("User not authenticated");

    set({ jobsError: null });

    try {
      const { domainId, pageId } = await ensureDomainAndPageExists(url);

      // The backend writes the placeholder Firestore doc and returns the job ID.
      // The realtime listener picks up the resulting document.
      const { job_id } = await JobGenerationService.startHeuristicJob({
        url,
        page_id: pageId,
        domain_id: domainId,
      });

      set({ currentJobId: job_id });

      return job_id;
    } catch (err: unknown) {
      console.error("Job creation error:", err);
      set({ jobsError: "Failed to start job" });
      throw err;
    }
  },

  unsubscribeJobs: () => {
    console.log("Unsubscribing from all jobs");
    const subs = get()._jobsSubscriptions;
    subs.forEach((unsub) => unsub());
    set({ _jobsSubscriptions: new Map() });
    // Note: We no longer clear the jobs state to maintain data across navigation
    console.log("Jobs unsubscribed, but jobs data preserved");
  },
});
